import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ActivityContext,
    ActivityKpiContext,
    getActivityKpiContexts,
    getKpiValueKey,
} from "@features/report/activity-entry/utils/activityEntryGroupHelpers";
import { mergeServerFormValues } from "@features/report/activity-entry/utils/activityEntryFormValues";
import {
    isCalculatedEntryMode,
    normalizeValueForInput,
} from "@core/reports/utils/fieldTypeUtils";
import type { SaveDraftPayload } from "@features/report/activity-entry/types/activityEntry";

export interface UseActivityEntryFormParams {
    enabled: boolean;
    /** `${scope}-${entityId}-${reportTemplateId}-${reportTagId}` — re-hydrates the form when it changes. */
    hydrationKey: string;
    allKpiContexts: ActivityKpiContext[];
    allActivityContexts: ActivityContext[];
    showInactive: boolean;
}

/**
 * Owns the activity entry form's editable values, the saved-baseline ref used for
 * dirty detection, hydration from server data, per-KPI change handling, and the unsaved
 * count. `markSaved` syncs the baseline after a successful draft save (called by
 * useActivityEntryDrafting). Extracted verbatim from ActivityEntryForm.
 */
export function useActivityEntryForm({
    enabled,
    hydrationKey,
    allKpiContexts,
    allActivityContexts,
    showInactive,
}: UseActivityEntryFormParams) {
    const [values, setValues] = useState<Record<string, string>>({});
    const initialValuesRef = useRef<Record<string, string>>({});
    const lastHydrationKeyRef = useRef<string>("");

    const initialValues = useMemo(() => {
        const map: Record<string, string> = {};
        allKpiContexts.forEach(({ subGroupId, activity, kpi }) => {
            const key = getKpiValueKey(subGroupId, activity.activity_id, kpi.kpi_id);
            map[key] = normalizeValueForInput(
                kpi.submission?.kpi_value,
                kpi.data_type?.data_type_name ?? ""
            );
        });
        return map;
    }, [allKpiContexts]);

    useEffect(() => {
        if (!enabled) {
            setValues({});
            initialValuesRef.current = {};
            lastHydrationKeyRef.current = "";
            return;
        }

        const hasInitialValues = Object.keys(initialValues).length > 0;
        const baselineIsEmpty = Object.keys(initialValuesRef.current).length === 0;
        const selectorsChanged = lastHydrationKeyRef.current !== hydrationKey;

        // hydrate when:
        // 1) selectors changed OR
        // 2) baseline empty AND we finally have data
        if (selectorsChanged) {
            setValues(initialValues);
            initialValuesRef.current = initialValues;
            lastHydrationKeyRef.current = hydrationKey;
            return;
        }

        if (baselineIsEmpty && hasInitialValues) {
            setValues(initialValues);
            initialValuesRef.current = initialValues;
            lastHydrationKeyRef.current = hydrationKey;
        }
    }, [enabled, hydrationKey, initialValues]);

    /**
     * Imperatively merge a post-save/submit form GET into local state.
     * `forceKeys` (calculated KPIs) always adopt the server value.
     */
    const applyServerValues = useCallback(
        (serverValues: Record<string, string>, forceKeys?: Iterable<string>) => {
            setValues((prev) => {
                const merged = mergeServerFormValues({
                    currentValues: prev,
                    baseline: initialValuesRef.current,
                    serverValues,
                    forceKeys,
                });
                initialValuesRef.current = merged.baseline;
                return merged.values;
            });
        },
        []
    );

    const handleChange = useCallback(
        (subGroupId: number | null, activityId: number, kpiId: number, v: string) => {
            const key = getKpiValueKey(subGroupId, activityId, kpiId);
            setValues((prev) => ({ ...prev, [key]: v }));
        },
        []
    );

    const isActivityDirty = useCallback(
        (context: ActivityContext): boolean => {
            return getActivityKpiContexts(context, showInactive).some(({ kpi }) => {
                if (isCalculatedEntryMode(kpi.entry_mode)) {
                    return false;
                }
                const key = getKpiValueKey(
                    context.subGroupId,
                    context.activity.activity_id,
                    kpi.kpi_id
                );
                const current = (values[key] ?? "").toString().trim();
                const original = (initialValuesRef.current[key] ?? "").toString().trim();
                return current !== original;
            });
        },
        [showInactive, values]
    );

    const unsavedCount = useMemo(() => {
        let count = 0;
        for (const context of allActivityContexts) {
            if (isActivityDirty(context)) count++;
        }
        return count;
    }, [allActivityContexts, isActivityDirty]);

    /** Sync the dirty-baseline with the values just persisted by a draft save. */
    const markSaved = useCallback((payload: SaveDraftPayload) => {
        payload.entries.forEach((entry) => {
            const key = getKpiValueKey(
                entry.report_sub_group_id ?? null,
                entry.activity_id,
                entry.kpi_id
            );
            initialValuesRef.current[key] = entry.kpi_value;
        });
    }, []);

    /**
     * Overwrite form values from Prefill without updating the dirty baseline,
     * so autosave / manual save treat them as unsaved.
     */
    const applyPrefillValues = useCallback((prefillValues: Record<string, string>) => {
        setValues((prev) => ({ ...prev, ...prefillValues }));
    }, []);

    return {
        values,
        setValues,
        initialValuesRef,
        handleChange,
        isActivityDirty,
        unsavedCount,
        markSaved,
        applyServerValues,
        applyPrefillValues,
    };
}
